import React from 'react';
import { Briefcase, Globe, Package, Truck, Shield, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Newsletter from '../components/Newsletter';

const LuxuryFashionProcurement = () => {
  const capabilities = [
    {
      icon: Globe,
      title: "Direct Access to European Fashion Houses",
      description: "Established relationships with premier Italian and Parisian brands for wholesale procurement and private buying"
    },
    {
      icon: Package,
      title: "Seasonal & Limited-Release Sourcing",
      description: "Priority access to new collections, limited editions, and exclusive pieces before they reach mass market"
    },
    {
      icon: Briefcase,
      title: "Boutique Stock Procurement",
      description: "Complete inventory sourcing for luxury retail operations, from opening stock to ongoing replenishment"
    },
    {
      icon: Award,
      title: "VIP Wardrobe Curation",
      description: "Personal shopping and wardrobe development for high-net-worth individuals and government dignitaries"
    },
    {
      icon: Truck,
      title: "International Logistics & Customs",
      description: "End-to-end shipping, customs clearance, and compliant import services from Europe to the Middle East"
    },
    {
      icon: Shield,
      title: "Authenticity Guarantee",
      description: "All products sourced directly from authorized channels with full authenticity verification"
    }
  ];

  const experience = [
    "10+ years sourcing directly from Milan and Paris",
    "Five luxury retail stores operated in Vancouver's premier malls",
    "Direct relationships with European fashion house buying offices",
    "Expert knowledge of buying cycles and seasonal planning",
    "Experience managing luxury brand standards and retail operations",
    "Complete understanding of import regulations and compliance",
    "Cultural expertise for GCC luxury market requirements",
    "Government gifting program procurement experience"
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with European Fashion District */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/2lss3g0d_8DDF2218-B0A3-4D8F-83E7-18BD6E30259C.png"
            alt="European Luxury Fashion District"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/65"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center py-32">
          <h1 className="luxury-heading text-5xl md:text-7xl text-white mb-8 leading-tight">
            Luxury Fashion Procurement
            <span className="text-[#C9A55A]"> – Milan & Paris</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed">
            10+ years of direct sourcing from Europe's fashion capitals.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              asChild
              className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7 smooth-transition shadow-xl"
            >
              <Link to="/contact">
                Request Sourcing Consultation
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold text-lg px-12 py-7 smooth-transition"
            >
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20 bg-black"></div>

      {/* Capabilities Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Our Fashion Procurement Capabilities</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              From wholesale buying to VIP curation, we deliver European luxury to the Middle East
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <Card key={index} className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 group cursor-pointer">
                <CardContent className="p-10">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center mb-8 group-hover:bg-[#C9A55A]/20 smooth-transition border border-[#C9A55A]/20">
                    <capability.icon className="text-[#C9A55A]" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{capability.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{capability.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* A Decade of European Sourcing */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                A Decade of European Sourcing Experience
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Before expanding into international consulting, Syndicate Consultants spent <span className="text-[#C9A55A] font-semibold">more than 10 years</span> building direct relationships with Italian and Parisian fashion houses.
                </p>
                <p className="text-lg">
                  We operated <span className="text-[#C9A55A] font-semibold">five luxury retail stores</span> across Vancouver's premier shopping centers, giving us unparalleled insight into:
                </p>
                <ul className="space-y-2 ml-6">
                  <li>• European fashion house buying cycles</li>
                  <li>• Seasonal collection planning and procurement</li>
                  <li>• Luxury brand standards and retail operations</li>
                  <li>• International logistics and customs compliance</li>
                  <li>• VIP client service and wardrobe curation</li>
                </ul>
                <p className="text-gray-400 italic pt-4">
                  Today, this foundation enables us to deliver high-volume sourcing solutions for boutiques, private clients, and government gifting programs across the GCC.
                </p>
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-semibold text-white mb-6">Proven Track Record</h3>
                  <div className="space-y-4">
                    {experience.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#C9A55A] text-xs font-bold">✓</span>
                        </div>
                        <span className="text-gray-300 leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Sourcing Process */}
      <section className="py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Our Sourcing Process</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              From initial consultation to final delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#C9A55A]/20">
                  <span className="text-[#C9A55A] text-2xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Consultation</h3>
                <p className="text-gray-400 text-sm">Understand your needs, budget, timeline, and brand preferences</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#C9A55A]/20">
                  <span className="text-[#C9A55A] text-2xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Sourcing</h3>
                <p className="text-gray-400 text-sm">Direct procurement from Milan and Paris fashion houses and showrooms</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#C9A55A]/20">
                  <span className="text-[#C9A55A] text-2xl font-bold">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Logistics</h3>
                <p className="text-gray-400 text-sm">International shipping, customs clearance, and compliant import handling</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#C9A55A]/20">
                  <span className="text-[#C9A55A] text-2xl font-bold">4</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Delivery</h3>
                <p className="text-gray-400 text-sm">Secure delivery with authenticity verification and complete documentation</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Who We Serve</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Luxury procurement for diverse clientele
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Luxury Boutiques</h3>
                <p className="text-gray-400 mb-4">Complete inventory procurement for high-end retail operations</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Opening stock sourcing</li>
                  <li>• Seasonal collections</li>
                  <li>• Ongoing replenishment</li>
                  <li>• Limited edition pieces</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Government Programs</h3>
                <p className="text-gray-400 mb-4">Discreet procurement for official gifting and protocol needs</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Diplomatic gifts</li>
                  <li>• Official wardrobe programs</li>
                  <li>• Cultural exchange items</li>
                  <li>• High-level protocol gifts</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Private Clients</h3>
                <p className="text-gray-400 mb-4">VIP wardrobe curation and personal shopping services</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Personal wardrobe development</li>
                  <li>• Seasonal updates</li>
                  <li>• Special occasion sourcing</li>
                  <li>• Exclusive access pieces</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Source European Luxury?
          </h2>
          <p className="text-xl text-gray-400 mb-8 leading-relaxed">
            Contact us for a confidential consultation on your luxury procurement needs
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7"
          >
            <Link to="/contact">Request Consultation</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default LuxuryFashionProcurement;